import {useEffect, useRef, useState} from "react";

import {URL, MODE_TYPE, AD_RANGE_OPTION, MANPLUS_PRE_ROLL_AD_SECTION_ID} from "utils";
import View from "./View";

const Container = () => {
  const LONG_VIDEO_SEC = 15;
  const SKIP_VIDEO_SEC = 5;
  const searchParams = new URLSearchParams(window.location.search);
  const isPlayMode = searchParams.get("mode") === MODE_TYPE.PLAY_MODE;

  const videoRef = useRef(null);
  const [playedVideoRange, setPlayedVideoRange] = useState(null);
  const [videoProgressState, setVideoProgressState] = useState(null);
  const [mediaData, setMediaData] = useState(null);
  const [isVideoHidden, setIsVideoHidden] = useState(false);
  const [isShowSkipBtn, setIsShowSkipBtn] = useState(false);
  const [countDown, setCountDown] = useState(LONG_VIDEO_SEC);

  const [billingProgressTime, setBillingProgressTime] = useState(0);
  const [completeProgressTime, setCompleteProgressTime] = useState(0);

  useEffect(() => {
    getVideoAdForManPlus();
  }, []);

  useEffect(() => {
    if (!playedVideoRange) return;
    fetchForTracking(mediaData.trackingEventApis[playedVideoRange]);
  }, [playedVideoRange]);

  useEffect(() => {
    if (!videoProgressState) return;
    fetchForTracking(mediaData.trackingEventApis[videoProgressState]);
  }, [videoProgressState]);

  const getVideoAdForManPlus = async () => {
    const videoWidth = window.innerWidth * 0.8;
    const videoHeight = videoWidth * (16 / 9);

    await fetch(`${URL.MANPLUS_AD}&a_section=${MANPLUS_PRE_ROLL_AD_SECTION_ID}&i_video_w=${videoWidth}&i_video_h=${videoHeight}`)
      .then(res => res.json())
      .then(data => {
        const inline = data.vast.ad[0].inline;
        const logoPath = inline.extensions.logo_img_path;
        const logoLandingUrl = inline.extensions.logo_landing_url;
        const impression = inline.impression;
        const {
          duration,
          mediafiles: {
            mediafile: {mediafile_src},
          },
          trackingevents: {tracking},
          videoclicks,
        } = inline.creatives.creative.linear;

        const trackingEventApis = {
          [tracking[0].event]: tracking[0].event_traking,
          [tracking[1].event]: tracking[1].event_traking,
          [tracking[2].event]: tracking[2].event_traking,
          [tracking[3].event]: tracking[3].event_traking,
          [tracking[4].event]: tracking[4].event_traking,
        };

        const skipApiExist = tracking[5].event === AD_RANGE_OPTION.SKIP;
        if (skipApiExist) {
          trackingEventApis[AD_RANGE_OPTION.SKIP] = tracking[5].event_traking;
        }

        const {event_traking: billingProgressApi, offset: billingProgressTime} = tracking[skipApiExist ? 6 : 5];
        const {event_traking: completeProgressApi, offset: completeProgressTime} = tracking[skipApiExist ? 7 : 6];

        trackingEventApis[AD_RANGE_OPTION.PROGRESS_BILLING] = billingProgressApi;
        trackingEventApis[AD_RANGE_OPTION.PROGRESS_COMPLETE] = completeProgressApi;

        setBillingProgressTime(Number(billingProgressTime.slice(-2)));
        setCompleteProgressTime(Number(completeProgressTime.slice(-2)));

        setMediaData({
          logoPath,
          logoLandingUrl,
          impression,
          trackingEventApis,
          durationTime: Number(duration.slice(-2)),
          videoclicks,
          mediafile_src,
        });
      })
      .catch(() => setIsVideoHidden(true));
  };

  const fetchForTracking = async requestUrl => {
    if (!requestUrl) return;
    await fetch(requestUrl);
  };

  const onLoadedMetadata = () => {
    fetchForTracking(mediaData.impression);
  };

  const onTimeUpdate = () => {
    const currentTime = Math.floor(videoRef.current.currentTime);

    if (billingProgressTime < currentTime) {
      setVideoProgressState(AD_RANGE_OPTION.PROGRESS_BILLING);
    }
    if (completeProgressTime < currentTime) {
      setVideoProgressState(AD_RANGE_OPTION.PROGRESS_COMPLETE);
    }
    if (SKIP_VIDEO_SEC <= currentTime) {
      setIsShowSkipBtn(true);
    }
    setCountDown(SKIP_VIDEO_SEC - currentTime);
    videoRef.current.setAttribute("controls", "controls");

    const playPercent = Math.floor((currentTime / mediaData.durationTime) * 100);
    if (playPercent === 100) {
      setPlayedVideoRange(AD_RANGE_OPTION.COMPLETE);
    } else if (playPercent >= 75) {
      setPlayedVideoRange(AD_RANGE_OPTION.THIRD_QUARTILE);
    } else if (playPercent >= 50) {
      setPlayedVideoRange(AD_RANGE_OPTION.MIDPOINT);
    } else if (playPercent >= 25) {
      setPlayedVideoRange(AD_RANGE_OPTION.FIRST_QUARTILE);
    } else if (playPercent < 25) {
      setPlayedVideoRange(AD_RANGE_OPTION.START);
    }
  };

  const onClickVideo = () => {
    const {clickthrough, clicktracking} = mediaData.videoclicks;
    fetchForTracking(clicktracking !== "" ? clicktracking : clickthrough);
    window.open(clickthrough, "_blank");
  };

  const hideVideo = () => {
    videoRef.current.pause();
    setIsVideoHidden(true);
  };

  const onClickSkipBtn = () => {
    fetchForTracking(mediaData.trackingEventApis[AD_RANGE_OPTION.SKIP]);
    hideVideo();
  };

  const onClickLogo = () => {
    window.open(mediaData.logoLandingUrl, "_blank");
  };

  return (
    <View
      videoRef={videoRef}
      countDown={countDown}
      mediaData={mediaData}
      isPlayMode={isPlayMode}
      isVideoHidden={isVideoHidden}
      isShowSkipBtn={isShowSkipBtn}
      hideVideo={hideVideo}
      onClickLogo={onClickLogo}
      onLoadedMetadata={onLoadedMetadata}
      onTimeUpdate={onTimeUpdate}
      onClickVideo={onClickVideo}
      onClickSkipBtn={onClickSkipBtn}
    />
  );
};

export default Container;
